import { TrendingUp, ArrowUpRight, ArrowRight, ArrowDownRight, Briefcase, TriangleAlert as AlertTriangle } from 'lucide-react';
import type { TabWhyItMatters } from '../../../../types/craft';
import FormattedText from '../../../common/FormattedText';

interface Props {
  data: TabWhyItMatters;
}

const IMPACT_STYLES = {
  high: { bg: 'bg-app-critical/10', text: 'text-app-critical', icon: ArrowUpRight, label: 'HIGH' },
  medium: { bg: 'bg-app-warning/10', text: 'text-app-warning', icon: ArrowRight, label: 'MED' },
  low: { bg: 'bg-app-canvas', text: 'text-app-muted', icon: ArrowDownRight, label: 'LOW' },
};

export default function WhyItMattersTab({ data }: Props) {
  if (!data?.strategic_importance && !data?.commercial_impact?.length) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-app-muted">No data available yet.</p>
        <p className="text-xs text-app-muted mt-1">Generate this module to populate content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {data.strategic_importance && (
        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-app-focus" />
            <h4 className="text-sm font-semibold text-app-ink">Strategic Importance</h4>
          </div>
          <FormattedText text={data.strategic_importance} className="text-sm text-app-ink" />
        </div>
      )}

      {data.commercial_impact?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-app-ink mb-3">Commercial Impact</h4>
          <div className="space-y-3">
            {data.commercial_impact.map((item, i) => {
              const impact = IMPACT_STYLES[item.impact_level] || IMPACT_STYLES.medium;
              const ImpactIcon = impact.icon;
              return (
                <div key={i} className={`rounded-lg border p-4 ${
                  item.impact_level === 'high' ? 'border-app-critical bg-app-surface shadow-none' : 'border-app-line bg-app-surface'
                }`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="text-sm text-app-ink leading-relaxed flex-1">{item.insight}</p>
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex-shrink-0 ${impact.bg} ${impact.text}`}>
                      <ImpactIcon className="w-2.5 h-2.5" />
                      {impact.label}
                    </span>
                  </div>
                  {item.confidence_score > 0 && (
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-16 h-1 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label="Evidence confidence" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(item.confidence_score * 100)}>
                        <div className="h-full bg-app-focus rounded-full" style={{ width: `${item.confidence_score * 100}%` }} />
                      </div>
                      <span className="text-[10px] text-app-muted">{Math.round(item.confidence_score * 100)}% confidence</span>
                    </div>
                  )}
                  {item.sales_translation && (
                    <div className="mt-2 bg-forest-50 border border-forest-100 rounded-lg px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-forest-600 mb-0.5">Sales Translation</p>
                      <p className="text-xs text-forest-800 leading-relaxed">{item.sales_translation}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.cost_of_ignorance && (
          <div className="bg-app-critical/10 border border-app-critical rounded-lg p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-app-critical" />
              <h4 className="text-sm font-semibold text-app-critical">Cost of Ignorance</h4>
            </div>
            <p className="text-xs text-app-critical leading-relaxed">{data.cost_of_ignorance}</p>
          </div>
        )}

        {data.career_impact && (
          <div className="bg-app-positive/10 border border-app-positive rounded-lg p-5">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-4 h-4 text-app-positive" />
              <h4 className="text-sm font-semibold text-app-positive">Career Impact</h4>
            </div>
            <p className="text-xs text-app-positive leading-relaxed">{data.career_impact}</p>
          </div>
        )}
      </div>
    </div>
  );
}
